import { Sparkles, Lock, CheckCircle2, Zap, AlertCircle } from "lucide-react";
import {
  DISCOUNT_TIERS,
  getCurrentTier,
  getNextTier,
  MOQ,
} from "@/lib/discountTiers";

/**
 * Gamified discount ladder. Shows all 8 tiers, current level, and progress to next unlock.
 * Props:
 *   totalQty: number — current/preview cart quantity
 *   variant: "pdp" | "drawer" — controls density
 */
export const DiscountLadder = ({ totalQty = 0, variant = "pdp" }) => {
  const current = getCurrentTier(totalQty);
  const next = getNextTier(totalQty);
  const compact = variant === "drawer";

  // Progress between current tier qty and next tier qty
  const fromQty = current ? current.qty : 0;
  const toQty = next ? next.qty : (current?.qty || 1);
  const progressPct = next
    ? Math.max(4, Math.min(100, ((totalQty - fromQty) / (toQty - fromQty)) * 100))
    : 100;

  const itemsToNext = next ? Math.max(0, next.qty - totalQty) : 0;
  const itemsToMoq = Math.max(0, MOQ - totalQty);
  const moqReached = totalQty >= MOQ;

  return (
    <div
      className={`relative overflow-hidden border border-neutral-200 bg-gradient-to-br from-amber-50 via-white to-white ${compact ? "p-4" : "p-5"}`}
      data-testid={`discount-ladder-${variant}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" strokeWidth={2} />
          <span className="text-[10px] tracking-[0.3em] uppercase font-black text-neutral-900">
            Bulk Rewards
          </span>
        </div>
        {current ? (
          <div
            className="inline-flex items-center gap-1.5 bg-black text-white text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 font-bold"
            data-testid="ladder-current-tier"
          >
            <span>{current.tone}</span>
            <span>{current.label} · {current.pct}% off</span>
          </div>
        ) : (
          <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
            Add 1 item to unlock 2%
          </span>
        )}
      </div>

      {/* Next-tier nudge */}
      {next ? (
        <div className="text-sm font-medium text-neutral-800 mb-2" data-testid="ladder-next-nudge">
          <Zap className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-amber-600" strokeWidth={2.5} />
          Add <span className="font-black text-black">{itemsToNext}</span> more item{itemsToNext === 1 ? "" : "s"} → unlock{" "}
          <span className="font-black text-black">{next.pct}% off</span>{" "}
          <span className="text-[11px] text-neutral-500">({next.label})</span>
        </div>
      ) : (
        <div className="text-sm font-bold text-emerald-700 mb-2 inline-flex items-center gap-1" data-testid="ladder-maxed">
          <CheckCircle2 className="w-4 h-4" /> Max reward unlocked — 15% off
        </div>
      )}

      {/* Progress bar */}
      <div className="relative h-2 bg-neutral-200 overflow-hidden mb-4">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-500"
          style={{ width: `${current ? progressPct : Math.min(8, totalQty * 8)}%` }}
          data-testid="ladder-progress"
        />
      </div>

      {/* Tier rail */}
      <div className={`grid grid-cols-8 gap-1 ${compact ? "" : "mb-3"}`} data-testid="ladder-rail">
        {DISCOUNT_TIERS.map((t) => {
          const unlocked = totalQty >= t.qty;
          const isCurrent = current && current.qty === t.qty;
          return (
            <div
              key={t.qty}
              className={`flex flex-col items-center text-center py-2 px-1 border ${
                isCurrent
                  ? "border-black bg-black text-white"
                  : unlocked
                  ? "border-amber-400 bg-amber-50 text-black"
                  : "border-neutral-200 bg-white text-neutral-400"
              }`}
              data-testid={`tier-${t.qty}`}
              title={`${t.qty}+ items → ${t.pct}% off`}
            >
              <div className="text-[10px] font-black leading-none">
                {unlocked ? t.tone : <Lock className="w-2.5 h-2.5 inline" />}
              </div>
              <div className={`mt-1 font-heading font-black tracking-tight ${compact ? "text-xs" : "text-sm"}`}>
                {t.pct}%
              </div>
              <div className={`mt-0.5 ${compact ? "text-[8px]" : "text-[9px]"} font-medium opacity-80`}>
                {t.qty}+
              </div>
            </div>
          );
        })}
      </div>

      {/* MOQ status row */}
      <div
        className={`mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between gap-3 text-xs ${
          moqReached ? "text-emerald-700" : "text-amber-700"
        }`}
        data-testid="ladder-moq"
      >
        <span className="flex items-center gap-1.5">
          {moqReached ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="font-bold">Ready to checkout</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-3.5 h-3.5" />
              <span>
                <span className="font-bold">{itemsToMoq} more</span> item{itemsToMoq === 1 ? "" : "s"} to enable{" "}
                <span className="font-bold">checkout</span>
              </span>
            </>
          )}
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold">MOQ {MOQ}</span>
      </div>
    </div>
  );
};
